import { Dialog, type Option, SingleSelect, Toast } from "@appretio/ui-kit";
import { type ReactNode, useEffect, useState } from "react";
import { formContainer } from "./CreateCubeModal.css";

/**
 * Props for the shared "create a new cube version" modal. Used by both
 * the forecast (``CreateUserCubeModal``) and budget (``CreateBudgetCubeModal``)
 * flows — the only caller-specific bits are the version-name field (which
 * differs in type / validation between the two) and the mutation itself.
 */
export interface CreateCubeModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /** Setter for the modal's open state. */
  onOpenChange: (open: boolean) => void;
  /** Dialog title. */
  title: string;
  /**
   * Optional caller-rendered field rendered above ``versionNameField``.
   * Used by the budget flow to expose a disabled Brand dropdown; omit
   * for the forecast flow.
   */
  brandField?: ReactNode;
  /**
   * Caller-rendered field(s) for the version name. The caller owns the
   * field's type (free input vs. dropdown), value, and its own error
   * state — see ``validateExtra`` to gate submission on version-name
   * validity.
   */
  versionNameField: ReactNode;
  /** Options for the "source producer" dropdown (Budget / DS / Other / ...). */
  sourceProducerOptions: Option[];
  /** Given the selected source producer value, returns available versions. */
  getSourceVersionOptions: (sourceProducerValue: string) => Option[];
  /**
   * Optional per-producer message shown when the version list is empty
   * (e.g. "Select one brand in the context filter to see budget versions").
   */
  getSourceVersionNoResultsText?: (
    sourceProducerValue: string
  ) => string | undefined;
  /**
   * Caller-side validation run before the built-in source validation.
   * Return ``false`` to abort the submit; the caller is responsible for
   * surfacing its own error UI on the ``versionNameField``.
   */
  validateExtra?: () => boolean;
  /**
   * Submit handler — called after validation passes. Resolves with the
   * new cube id on success, or ``null`` on failure (the modal will show
   * the error toast and leave itself open for retry).
   */
  onSubmit: (args: {
    sourceProducer: Option;
    sourceVersion: Option;
  }) => Promise<{ cubeId: string } | null>;
  /** Notified with the new cube id after a successful submit. */
  onCubeCreated: (cubeId: string) => void;
  /** Whether the submit mutation is in flight — controls the Dialog's loading state. */
  isPending: boolean;
  /** Toast title on success. */
  successToastTitle: string;
  /** Toast title on error. */
  errorToastTitle: string;
}

/**
 * Shared create-cube modal. Owns the toast, source-producer/version
 * SingleSelects, validation of those two fields, and the submit flow.
 * The version-name field is caller-rendered to accommodate forecast's
 * disabled date input and budget's FY-label dropdown.
 */
export function CreateCubeModal({
  open,
  onOpenChange,
  title,
  brandField,
  versionNameField,
  sourceProducerOptions,
  getSourceVersionOptions,
  getSourceVersionNoResultsText,
  validateExtra,
  onSubmit,
  onCubeCreated,
  isPending,
  successToastTitle,
  errorToastTitle
}: CreateCubeModalProps) {
  const [toast, setToast] = useState<{
    open: boolean;
    variant: "success" | "error";
    title: string;
  }>({ open: false, variant: "success", title: "" });

  const [sourceProducer, setSourceProducer] = useState<Option | undefined>();
  const [sourceVersion, setSourceVersion] = useState<Option | undefined>();
  const [errors, setErrors] = useState<{
    sourceProducer?: string;
    sourceVersion?: string;
  }>({});

  // Reset when the modal opens.
  useEffect(() => {
    if (open) {
      setSourceProducer(undefined);
      setSourceVersion(undefined);
      setErrors({});
    }
  }, [open]);

  const sourceVersionOptions = sourceProducer
    ? getSourceVersionOptions(sourceProducer.value)
    : [];
  const sourceVersionNoResultsText =
    sourceProducer && getSourceVersionNoResultsText
      ? getSourceVersionNoResultsText(sourceProducer.value)
      : undefined;

  function validateSources() {
    const next: typeof errors = {};
    if (!sourceProducer) next.sourceProducer = "Source producer is required";
    if (sourceProducer && !sourceVersion)
      next.sourceVersion = "Source version is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleConfirm() {
    if (validateExtra && !validateExtra()) return false;
    if (!validateSources()) return false;
    if (!sourceProducer || !sourceVersion) return false;

    const result = await onSubmit({ sourceProducer, sourceVersion });
    if (result) {
      onCubeCreated(result.cubeId);
      onOpenChange(false);
      setToast({ open: true, variant: "success", title: successToastTitle });
    } else {
      setToast({ open: true, variant: "error", title: errorToastTitle });
    }
    return false;
  }

  return (
    <>
      <Toast
        open={toast.open}
        setOpen={(open) => setToast((prev) => ({ ...prev, open }))}
        variant={toast.variant}
        title={toast.title}
      />
      <Dialog
        hideFeedbackIcon
        open={open}
        title={title}
        onOpenChange={onOpenChange}
        confirmText="Create"
        cancelText="Cancel"
        onConfirm={handleConfirm}
        isLoading={isPending}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleConfirm();
          }}
        >
          <div className={formContainer}>
            {brandField}
            {versionNameField}

            <SingleSelect
              placeholder="Source producer"
              options={sourceProducerOptions}
              selectedOption={sourceProducer}
              onChange={(value) => {
                setSourceProducer(
                  sourceProducerOptions.find((option) => option.value === value)
                );
                setSourceVersion(undefined);
                setErrors((prev) => ({ ...prev, sourceProducer: undefined }));
              }}
              error={errors.sourceProducer}
              disabled={isPending}
              size="md"
              modalPopover
            />

            {sourceProducer && (
              <SingleSelect
                placeholder="Source version"
                options={sourceVersionOptions}
                selectedOption={sourceVersion}
                onChange={(value) => {
                  setSourceVersion(
                    sourceVersionOptions.find(
                      (option) => option.value === value
                    )
                  );
                  setErrors((prev) => ({ ...prev, sourceVersion: undefined }));
                }}
                error={errors.sourceVersion}
                noResultsText={sourceVersionNoResultsText}
                disabled={isPending}
                size="md"
                modalPopover
              />
            )}
          </div>
        </form>
      </Dialog>
    </>
  );
}
